import 'draft-js/dist/Draft.css';
import './RichTextInput.css';
import './Decorators/Decorators.css';
import React from 'react';
import {
    EditorState, Editor, getDefaultKeyBinding, EditorCommand, DraftHandleValue, CompositeDecorator,
    RichUtils
} from 'draft-js';
import { IFormInputProps } from '../../interfaces/IFormInputProps';
import { IRichTextInputToolbar, RichTextInputToolbar, RichtextToolbarButtonClassName } from './RichTextInputToolbar';
import { findLinkDecoratorEntities, LinkDecorator } from './Decorators/LinkDecorator';
import { IFormInputComponent } from '../../interfaces/IFormInputComponent';
import { toRichtext, fromHtml, isEquialsStates, getMultilineValue, RichTextValue, customStyleMap, isMacOS, getMultilineHtml, Richtext, maxTabDepth } from './common';
import { useDebounce, useOuterClick, useResizeObject } from '../../../utils/effects';
import { ActionButton, css, getId } from 'office-ui-fabric-react';
import { RichtextEditorCommands, HotKey } from './commands';
import InputErrorMessage from '../inputErrorMessage';

type RichTextInputProps = IFormInputProps<RichTextValue, HTMLTextAreaElement> & {
    placeholder?: string;
    containerMaxHeight?: number;
    hideShowMoreButtons?: boolean;
}

const decorator = new CompositeDecorator([
    {
        strategy: findLinkDecoratorEntities,
        component: LinkDecorator,
    },
]);

const onChangeDebounceDelay: number = 0;
const onClickDebounceDelay: number = 300;
const DefaultContainerMaxHeight = 116;

const buildEditorState = (value?: RichTextValue): EditorState => !value
    ? EditorState.createEmpty(decorator)
    : EditorState.createWithContent(fromHtml(getMultilineValue(value)), decorator);

const RichTextInput = React.forwardRef<IFormInputComponent, RichTextInputProps>((props, ref) => {
    const domEditor = React.useRef<Editor | null>(null);
    const toolbar = React.useRef<IRichTextInputToolbar | null>(null);

    const [editorState, setEditorState] = React.useState<EditorState>(buildEditorState(props.value));
    const [richValue, setRichValue] = React.useState(props.value);

    const getErrorMessage = (value: RichTextValue): string | undefined => props.validator?.getErrorMessage(value);
    const [errorMessage, setErrorMessage] = React.useState(getErrorMessage(props.value));

    const updateErrorMessage = React.useCallback((value: RichTextValue) => {
        const newErrorMessage = getErrorMessage(value);
        if (errorMessage !== newErrorMessage) {
            setErrorMessage(newErrorMessage)
        }
    }, [props.validator, errorMessage]);

    React.useEffect(() => {
        if (getMultilineHtml(richValue) !== getMultilineHtml(props.value)) {
            setEditorState(buildEditorState(props.value));
            updateErrorMessage(props.value);
        }
    }, [props.value]);

    React.useEffect(() => updateErrorMessage(props.value), [props.validator]);

    const [inEditMode, setInEditMode] = React.useState<boolean>(false);
    const [isDirty, setIsDirty] = React.useState<boolean>(false);

    const [showMoreClicked, setShowMoreClicked] = React.useState(false);
    const [containerHeight, setContainerHeight] = React.useState(0);

    const controlId = React.useMemo(() => props.id ?? getId(), [props.id]);
    const readonly = React.useMemo(() => !!props.disabled || !!props.readOnly, [props.disabled, props.readOnly]);

    const focus = React.useCallback(() => {
        !readonly && !inEditMode && setInEditMode(true);
        setTimeout(() => domEditor.current?.focus(), 100);
    }, [domEditor, inEditMode, readonly]);

    React.useImperativeHandle(ref, (): IFormInputComponent => ({ focus: () => focus() }));

    const onEditComplete = React.useCallback(() => {
        if (readonly) {
            return;
        }
        if (!inEditMode) {
            return;
        }

        const newValue = toRichtext(editorState);
        if (isDirty && props.validator?.isValid(newValue)) {
            props.onEditComplete?.(newValue);
        }
        if (!newValue.html) {
            setEditorState(buildEditorState(newValue));
        }

        setIsDirty(false);
        setTimeout(() => setInEditMode(false), onClickDebounceDelay);
    }, [props.value, inEditMode, editorState, isDirty, readonly]);

    const handleKeyCommand = React.useCallback((command: EditorCommand, state: EditorState): DraftHandleValue => {
        const cmd = Object.values(RichtextEditorCommands).find(_ => _.style?.toLocaleLowerCase() === command.toLocaleLowerCase());
        if (!cmd?.onCommand) {
            return 'not-handled';
        }

        if (command === RichtextEditorCommands.link.style) {
            toolbar.current?.openLinkEditor();
        } else {
            onChange(cmd.onCommand(state))
        }
        return 'handled';
    }, [readonly, editorState, toolbar]);

    const keyBindingFn = React.useCallback((e: React.KeyboardEvent<{}>): EditorCommand | null => {
        if (e.key === "Tab" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
            onEditComplete();
            return null;
        }

        const cmd = Object.values(RichtextEditorCommands).find(_ => isHotKeyPressed(_.hotKey, e));
        return cmd?.style ?? getDefaultKeyBinding(e);
    }, [readonly, onEditComplete]);

    const onChanged = useDebounce((value: Richtext) => {
        setRichValue(value);
        updateErrorMessage(value);
        props.onChanged?.(value);
    }, onChangeDebounceDelay);

    const onChange = React.useCallback((state?: EditorState) => {
        if (readonly || !state) {
            return;
        }

        if (isDirty || !isEquialsStates(editorState, state)) {
            setEditorState(state);
            if (!isDirty) {
                setIsDirty(true);
            }
            onChanged?.(toRichtext(state));
        } else {
            setEditorState(state);
        }
    }, [readonly, editorState, isDirty, props.onChanged]);

    const containerRef = useOuterClick<HTMLDivElement>(readonly ? undefined : onEditComplete, [controlId, "toolbar", RichtextToolbarButtonClassName]);

    const containerMaxHeight = React.useMemo(() => props.containerMaxHeight ?? DefaultContainerMaxHeight, [props.containerMaxHeight]);
    const refEditor = useResizeObject<HTMLDivElement>((offsetWidth: number, offsetHeight: number) => setContainerHeight(offsetHeight));

    const isShowMoreBtnPanelVisible = React.useMemo(
        () => !props.hideShowMoreButtons && !inEditMode && containerHeight > containerMaxHeight
        , [props.hideShowMoreButtons, inEditMode, containerHeight, containerMaxHeight]);

    const toggleShowMoreClicked = React.useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowMoreClicked(!showMoreClicked);
    }, [inEditMode, showMoreClicked]);

    const onTab = React.useCallback((e: React.KeyboardEvent<{}>) => {
        e.preventDefault();
        const newEditorState = RichUtils.onTab(e, editorState, maxTabDepth);
        if (editorState === newEditorState) {
            return;
        }

        onChange(newEditorState);
    }, [onChange, editorState]);


    return <div className='richtext-wrapper'>
        <div id={controlId}
            className={css(
                props.className,
                'richtext',
                inEditMode ? 'in-edit-mode' : '',
                isShowMoreBtnPanelVisible && !showMoreClicked ? 'show-more-on' : '',
                { invalid: !!errorMessage }
            )}
            ref={containerRef}
            aria-hidden={true}
            style={!showMoreClicked && !inEditMode
                ? { maxHeight: containerMaxHeight }
                : undefined}
            onMouseDown={readonly
                ? (e) => {
                    e.stopPropagation();
                    setShowMoreClicked(true);
                }
                : undefined}
            onClick={(e) => {
                if(!readonly) {
                    e.stopPropagation();
                    focus();
                }
            }}
        >
            {!readonly && inEditMode && <RichTextInputToolbar
                ref={toolbar}
                editorState={editorState}
                onCommand={(newEditorState) => {
                    onChange(newEditorState);
                    focus();
                }} />}
            <div ref={refEditor} className='editor' >
                <Editor
                    ref={domEditor}
                    readOnly={readonly}
                    placeholder={props.placeholder}
                    editorState={editorState}
                    customStyleMap={customStyleMap}
                    handleKeyCommand={handleKeyCommand}
                    keyBindingFn={keyBindingFn}
                    onChange={onChange}
                    onFocus={() => !readonly && !inEditMode && setInEditMode(true)}
                    preserveSelectionOnBlur
                    spellCheck={inEditMode}
                    onTab={!readonly ? onTab : undefined}
                />
            </div>
        </div>
        <InputErrorMessage text={errorMessage} onErrorRender={props.onErrorRender} />
        {isShowMoreBtnPanelVisible
            && <div className={`show-more-btn-panel ${!showMoreClicked ? 'show-more-on' : ''}`}>
                <ActionButton
                    className="show-more-btn hyperlink-color"
                    text={showMoreClicked ? "Show less" : "Show more"}
                    onClick={e => toggleShowMoreClicked(e)}
                />
            </div>}
    </div>;
})

export default RichTextInput;

const isHotKeyPressed = (hotKey: HotKey | undefined, event: React.KeyboardEvent<{}>): boolean => {
    if (!hotKey || !event) {
        return false;
    }

    return (event.keyCode ? event.keyCode === hotKey.keyCode : event.key === hotKey.key)
        && (isMacOS()
            ? (event.altKey === !!hotKey.macos.option && event.metaKey === !!hotKey.macos.cmd)
            : (event.altKey === !!hotKey.win.alt && event.ctrlKey === !!hotKey.win.ctrl)
        );
}